import { createApi } from '@reduxjs/toolkit/query/react';
import moment from 'moment/moment';
import baseQueryWithTokenManagement from 'pages/auth/apiSlice';
import { supplierApi } from 'api/supplier';
import { itemApi } from 'api/item';

export const brandApi = createApi({
  tagTypes: [
    'BRAND',
    'BRAND_LIST',
    'PRICE_LIST',
    'BRAND_TAGS',
    'BRAND_PRICING_SUPPLIER',
    'BRAND_SUPPLIER_LIST',
    'BRAND_SUPPLIER_SELECTED_LIST',
  ],
  reducerPath: 'brandApi',
  baseQuery: baseQueryWithTokenManagement,
  endpoints: (builder) => ({
    brandCreate: builder.mutation<any, any>({
      query: (data) => ({
        url: '/wrd/api/brand',
        method: 'post',
        body: data,
      }),
      invalidatesTags: () => ['BRAND_LIST'],
    }),
    brandList: builder.query<any, any>({
      query: (args) => {
        const { size = 10, page = 1, sortField, sortOrder, search, filters } = args;
        const number = page - 1;
        let sortParam = '';
        if (sortField && sortOrder) {
          sortParam = `&sort=${sortField},${sortOrder}`;
        }
        const params = { ...filters, ...(typeof search !== 'undefined' && { search }) };
        return {
          url: `/wrd/api/brand/list?size=${size}&page=${number}${sortParam}`,
          params: Object.keys(params).length ? params : undefined,
        };
      },
      transformResponse: (response: any) => {
        // Transform the response to the desired shape
        const content = response?.data?.content;
        if (Array.isArray(content)) {
          content[0].brands = (response?.data?.content[0].brands || []).map((item: any) => {
            const managers = content[0].managers;
            const salesManagers: string[] =
              item.salesManagerIds?.map((id: string) => {
                return `${managers[id]?.firstName || ''} ${managers[id]?.lastName || ''}`;
              }) || [];
            return {
              ...item,
              formattedLastModifiedDate: {
                full: moment.utc(item.lastModifiedDate).format('DD.MM.YYYY HH:mm:ss'),
                day: moment.utc(item.lastModifiedDate).format('DD.MM.YYYY'),
              },
              formattedCreatedDate: {
                full: moment.utc(item.createDate).format('DD.MM.YYYY HH:mm:ss'),
                day: moment.utc(item.createDate).format('DD.MM.YYYY'),
              },
              ...(item.lastModifiedDate && {
                formattedLastUpdatedDate: {
                  full: moment.utc(item.lastModifiedDate).format('DD.MM.YYYY HH:mm:ss'),
                  day: moment.utc(item.lastModifiedDate).format('DD.MM.YYYY'),
                },
              }),
              ...(item.priceListExpirationDate && {
                formattedPricelistValidityDate: {
                  full: moment.utc(item.priceListExpirationDate).format('DD.MM.YYYY HH:mm:ss'),
                  day: moment.utc(item.priceListExpirationDate).format('DD.MM.YYYY'),
                },
              }),
              purchaseManager: managers?.[item.purchasingManagerId] || {},
              salesManagers,
              key: item.id,
              label: item.name,
              value: item.id,
            };
          });
        }
        return response;
      },
      providesTags: () => ['BRAND_LIST'],
    }),
    brandSelectList: builder.query<any, void>({
      query: () => {
        return {
          url: `/wrd/api/brand/selector-list`,
        };
      },
      transformResponse: (response: any) => {
        // Transform the response to the desired shape
        response.data = (response?.data || []).map((item: any) => ({
          ...item,
          label: item.name,
          value: item.id,
        }));
        return response.data;
      },
      providesTags: () => ['BRAND_LIST'],
    }),
    bradManagement: builder.query<any, any>({
      query: ({ id }) => {
        return {
          url: `/wrd/api/brand/management/${id}`,
        };
      },
      transformResponse: (response: any) => {
        // Transform the response to the desired shape
        return response.data;
      },
      providesTags: () => ['BRAND'],
    }),
    brandManagementUpdate: builder.mutation<any, any>({
      query: (body) => ({
        url: '/wrd/api/brand/brand-management',
        method: 'post',
        body,
      }),
      invalidatesTags: () => ['BRAND', 'BRAND_LIST'],
    }),
    brandSalesManagerCreate: builder.mutation<any, any>({
      query: (body) => ({
        url: '/wrd/api/brand/sales-manager',
        method: 'post',
        body,
      }),
      invalidatesTags: () => ['BRAND', 'BRAND_LIST'],
    }),
    brandSalesManagerUpdate: builder.mutation<any, any>({
      query: (body) => ({
        url: '/wrd/api/brand/sales-manager',
        method: 'put',
        body,
      }),
      invalidatesTags: () => ['BRAND', 'BRAND_LIST'],
    }),
    brandSalesManagerDelete: builder.mutation<any, any>({
      query: (body) => {
        return {
          url: `/wrd/api/brand/sales-manager`,
          method: 'delete',
          body,
        };
      },
      invalidatesTags: () => ['BRAND', 'BRAND_LIST'],
    }),
    brandById: builder.query<any, { id: string }>({
      query: (args) => {
        const { id } = args;
        return {
          url: `/wrd/api/brand/get-by-id?brandId=${id}`,
        };
      },
      transformResponse: (response: any) => {
        // Transform the response to the desired shape
        return {
          ...response.data,
          formattedLastModifiedDate: moment(response.data.lastModifiedDate).format('DD.MM.YYYY HH:mm:ss'),
          formattedCreatedDate: moment(response.data.createDate).format('DD.MM.YYYY HH:mm:ss'),
        };
      },
      providesTags: () => ['BRAND'],
    }),
    brandDetailsUpdate: builder.mutation<any, any>({
      query: (data) => ({
        url: '/wrd/api/brand/details',
        method: 'put',
        body: data,
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(supplierApi.util.invalidateTags(['COMPETITOR_LIST', 'SUPPLIER_PRICE_LIST']));
        dispatch(itemApi.util.invalidateTags(['ITEM_LIST', 'ITEM']));
      },
      invalidatesTags: () => ['BRAND', 'BRAND_LIST', 'BRAND_TAGS'],
    }),
    fetchPriceLists: builder.query<
      any,
      {
        search?: string;
        brandId: string;
        page: string | number;
        size: string | number;
        sort?: string;
        supplierId?: string;
        createdDateFrom?: string;
        createdDateTo?: string;
        lastUpdatedDateFrom?: string;
        lastUpdatedDateTo?: string;
        expireDateFrom?: string;
        expireDateTo?: string;
      }
    >({
      query: ({
        page,
        size,
        sort,
        search,
        brandId,
        supplierId,
        createdDateFrom,
        createdDateTo,
        lastUpdatedDateFrom,
        lastUpdatedDateTo,
        expireDateFrom,
        expireDateTo,
      }) => ({
        url: `/wrd/api/price-list/${brandId}/list`,
        params: {
          page,
          size,
          search,
          sort,
          supplierId,
          createdDateFrom,
          createdDateTo,
          lastUpdatedDateFrom,
          lastUpdatedDateTo,
          expireDateFrom,
          expireDateTo,
        },
      }),
      transformResponse: (response: any) => ({
        data: response.data,
        total: response.total,
      }),
      providesTags: () => ['PRICE_LIST'],
    }),
    priceListDelete: builder.mutation<any, any>({
      query: (body) => {
        return {
          url: `/wrd/api/price-list`,
          method: 'delete',
          body,
        };
      },
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(
          supplierApi.util.invalidateTags([
            'SUPPLIER_PRICE_LIST',
            'SUPPLIER_SELECTED_BRANDS',
            'SUPPLIER',
            'SUPPLIER_LIST',
          ])
        );
      },
      invalidatesTags: () => ['PRICE_LIST', 'BRAND', 'BRAND_LIST'],
    }),
    priceListCreate: builder.mutation<any, any>({
      query: (body) => {
        return {
          url: `/wrd/api/price-list`,
          method: 'post',
          body,
        };
      },
      invalidatesTags: () => ['PRICE_LIST', 'BRAND', 'BRAND_LIST'],
    }),
    brandTags: builder.query<any, void>({
      query: () => ({
        url: '/wrd/api/brand/get-tags',
      }),
      transformResponse: (response: any) => {
        // Transform the response to the desired shape
        return response?.data || [];
      },
      providesTags: () => ['BRAND_TAGS'],
    }),
    brandPricingSupplier: builder.query<any, { brandId: string }>({
      query: ({ brandId }) => ({
        url: `/wrd/api/brand/${brandId}/pricing-supplier`,
      }),
      transformResponse: (response: any) => {
        // Transform the response to the desired shape
        const mainSupplier = response?.data?.mainSupplier;
        const pricingReferenceSupplier = response?.data?.pricingReferenceSupplier;
        if (pricingReferenceSupplier) {
          pricingReferenceSupplier.supplierId = pricingReferenceSupplier.id;
        }
        if (mainSupplier) {
          mainSupplier.formattedPriceListUpdatedDate = {
            full: mainSupplier.priceListUpdated
              ? moment(mainSupplier.priceListUpdated).format('DD.MM.YYYY HH:mm:ss')
              : '',
            day: mainSupplier.priceListUpdated ? moment(mainSupplier.priceListUpdated).format('DD.MM.YYYY') : '',
          };
        }
        return response?.data || {};
      },
      providesTags: () => ['BRAND_PRICING_SUPPLIER'],
    }),
    pricingRulesUpdate: builder.mutation<any, any>({
      query: (body) => {
        return {
          url: `/wrd/api/brand/pricing-rules`,
          method: 'put',
          body,
        };
      },
      invalidatesTags: () => ['BRAND_PRICING_SUPPLIER', 'BRAND', 'BRAND_LIST'],
    }),
    // Brand Supplier
    brandSupplierList: builder.query<any, any>({
      query: (args) => {
        const { brandId, size = 10, page = 1, sortField, sortOrder, search, filters } = args;
        const number = page - 1;
        let sortParam = '';
        if (sortField && sortOrder) {
          sortParam = `&sort=${sortField},${sortOrder}`;
        }
        const params = { ...filters, ...(typeof search !== 'undefined' && { search }) };
        return {
          url: `/wrd/api/brand/${brandId}/supplier/list?size=${size}&page=${number}${sortParam}`,
          params: Object.keys(params).length ? params : undefined,
        };
      },
      transformResponse: (response: any) => {
        // Transform the response to the desired shape
        response.data.content = (response?.data?.content || []).map((_item: any) => ({
          ..._item,
          formattedPriceListUpdatedDate: {
            full: _item.priceListUpdated ? moment(_item.priceListUpdated).format('DD.MM.YYYY HH:mm:ss') : '',
            day: _item.priceListUpdated ? moment(_item.priceListUpdated).format('DD.MM.YYYY') : '',
          },
        }));
        return response.data;
      },
      providesTags: () => ['BRAND_SUPPLIER_LIST'],
    }),

    brandSupplierAdd: builder.mutation<any, any>({
      query: (body) => {
        return {
          url: `/wrd/api/brand/add-suppliers`,
          method: 'put',
          body,
        };
      },
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(
          supplierApi.util.invalidateTags([
            'COMPETITOR_LIST',
            'SUPPLIER_SPECIAL_PRICING_RULES',
            'SUPPLIER_SELECTED_BRANDS',
          ])
        );
      },
      invalidatesTags: () => [
        'BRAND_SUPPLIER_LIST',
        'BRAND_SUPPLIER_SELECTED_LIST',
        'BRAND',
        'BRAND_LIST',
        'BRAND_PRICING_SUPPLIER',
      ],
    }),
    brandSupplierRemove: builder.mutation<any, any>({
      query: (body) => {
        return {
          url: `/wrd/api/brand/supplier`,
          method: 'delete',
          body,
        };
      },
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(
          supplierApi.util.invalidateTags([
            'COMPETITOR_LIST',
            'SUPPLIER_SPECIAL_PRICING_RULES',
            'SUPPLIER_SELECTED_BRANDS',
          ])
        );
      },
      invalidatesTags: () => [
        'BRAND_SUPPLIER_LIST',
        'BRAND_PRICING_SUPPLIER',
        'BRAND_SUPPLIER_SELECTED_LIST',
        'BRAND',
        'BRAND_LIST',
      ],
    }),
    brandSupplierSelectedList: builder.query<any, any>({
      query: (args) => {
        const { brandId } = args;
        return {
          url: `/wrd/api/brand/${brandId}/selected-suppliers`,
        };
      },
      transformResponse: (response: any) => {
        // Transform the response to the desired shape
        return response.data;
      },
      providesTags: () => ['BRAND_SUPPLIER_SELECTED_LIST'],
    }),
    brandSupplierSelectList: builder.query<any, any>({
      query: (args) => {
        const { brandId } = args;
        return {
          url: `/wrd/api/brand/${brandId}/supplier/selector-list`,
        };
      },
      transformResponse: (response: any) => {
        // Transform the response to the desired shape
        response.data = (response?.data || []).map((item: any) => ({
          ...item,
          label: item.name,
          value: item.id,
        }));
        return response.data;
      },
      providesTags: () => ['BRAND_SUPPLIER_SELECTED_LIST'],
    }),
    brandMainSupplierSet: builder.mutation<any, any>({
      query: (body) => {
        return {
          url: `/wrd/api/brand/main-supplier`,
          method: 'put',
          body,
        };
      },
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(itemApi.util.invalidateTags(['ITEM_LIST', 'ITEM']));
      },
      invalidatesTags: () => ['BRAND_SUPPLIER_LIST', 'BRAND_PRICING_SUPPLIER', 'BRAND', 'BRAND_LIST'],
    }),
    brandMainSupplierReset: builder.mutation<any, any>({
      query: ({ brandId }) => {
        return {
          url: `/wrd/api/brand/${brandId}/main-supplier`,
          method: 'delete',
        };
      },
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(itemApi.util.invalidateTags(['ITEM_LIST', 'ITEM']));
      },
      invalidatesTags: () => ['BRAND_SUPPLIER_LIST', 'BRAND_PRICING_SUPPLIER', 'BRAND', 'BRAND_LIST'],
    }),
    brandSupplierStar: builder.mutation<any, any>({
      query: (body) => {
        return {
          url: `/wrd/api/brand/supplier/star`,
          method: 'put',
          body,
        };
      },
      invalidatesTags: () => ['BRAND_SUPPLIER_LIST', 'BRAND', 'BRAND_LIST'],
    }),

    // Brand Price reference Supplier
    brandPriceReferenceSupplierUpdate: builder.mutation<any, any>({
      query: (body) => {
        return {
          url: `/wrd/api/brand/price-reference-supplier`,
          method: 'put',
          body,
        };
      },
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(itemApi.util.invalidateTags(['ITEM_LIST', 'ITEM']));
      },
      invalidatesTags: () => ['BRAND_PRICING_SUPPLIER', 'BRAND', 'BRAND_LIST'],
    }),
  }),
});

export const {
  useBrandListQuery,
  useBrandCreateMutation,
  useBradManagementQuery,
  useBrandManagementUpdateMutation,
  useBrandSalesManagerCreateMutation,
  useBrandSalesManagerUpdateMutation,
  useBrandSalesManagerDeleteMutation,
  useBrandByIdQuery,
  useBrandDetailsUpdateMutation,
  useFetchPriceListsQuery,
  usePriceListDeleteMutation,
  usePriceListCreateMutation,
  useBrandSelectListQuery,
  useBrandTagsQuery,
  usePricingRulesUpdateMutation,
  useBrandPricingSupplierQuery,
  useBrandSupplierListQuery,
  useBrandSupplierAddMutation,
  useBrandSupplierSelectedListQuery,
  useBrandMainSupplierSetMutation,
  useBrandMainSupplierResetMutation,
  useBrandSupplierRemoveMutation,
  useBrandSupplierStarMutation,
  useBrandPriceReferenceSupplierUpdateMutation,
  useBrandSupplierSelectListQuery,
} = brandApi;
